/**
 * 企业用户域纯函数核心（client 无关，node --experimental-strip-types 可直测）。
 *
 * 拆分先例：kycFines.ts / kycFinesData.ts —— ./client 因 import.meta.env 顶层求值
 * 无法在 node 直跑导入，凡需可执行断言的查询构建 / 头映射 / 错误分类 / 状态文案
 * 一律收敛到本文件；corporate.ts 仅做请求薄层（走 merchantGet/Post/Put/Delete 唯一边界）。
 *
 * 契约真源：docs/PRD/allinks/backend/_shared/corporate-api.md §4.1（ONB-001~006）；
 * 后端实况锚点：CorporateOnboardingController / CorporateOnboardingAppService /
 * CorporateOnboardingItemView / CorporateCustomerItemView（BIGINT 一律 string，
 * long 基础类型同样被 MerchantPortalJacksonConfig ToString 序列化）。
 *
 * qa-report 遗留2：状态/维度「码→中文」映射表升格为 ../lib/merchantDictExt 的 Dict
 * （label+tone，键=后端 corporate 枚举 name()），本文件映射函数仅保留查表薄层。
 * 注意：本文件被 node --experimental-strip-types 直测导入，此处只能用相对路径引入
 * 无别名值依赖的模块（merchantDictExt 运行时零 import，type-only 不受别名影响）。
 */
import {
  CORP_APPROVAL_STATUS,
  CORP_BILL_DIMENSION,
  CORP_BILL_STATUS,
  CORP_BUDGET_STATUS,
  CORP_BUDGET_TYPE,
  CORP_DOC_STATUS,
  CORP_EXPENSE_STATUS,
  CORP_KYB_STATUS,
  CORP_MEMBER_ROLE,
  CORP_MEMBER_STATUS,
  CORP_ONBOARDING_STATUS,
  CORP_PLATFORM_STATUS,
  CORP_RISK_RULE_SCOPE,
  CORP_RISK_RULE_STATUS,
  CORP_RISK_RULE_TYPE,
} from '../lib/merchantDictExt.ts';

// ================= T1 错误文案兜底 =================

/**
 * 空 error 兜底：服务未返回失败原因时的统一文案。
 * （此前为无信息量的「操作失败」；error 非空时一律透出后端 message 原文，行为不变。）
 */
const EMPTY_ERROR_FALLBACK = '服务未返回失败原因，请稍后重试；若持续失败请联系平台并附操作时间';

// ================= 查询构建 =================

export type CorporateOnboardingListQuery = {
  /** 契约：枚举名（或 TINYINT code）；支持逗号多值（后端 parseStatuses） */
  status?: string | string[];
  keyword?: string;
  /** ISO-8601 日期（YYYY-MM-DD，LocalDate 闭区间） */
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type CorporateCustomerListQuery = {
  keyword?: string;
  /** 契约枚举名 NONE/VALID/INCOMPLETE/REJECTED；后端 @RequestParam 收 Integer code */
  kybStatus?: string;
  /** ISO-8601 日期（YYYY-MM-DD，LocalDate 闭区间；后端 parseDate 与 ONB-001 同一解析） */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

function appendParam(params: string[], key: string, value: string | number) {
  params.push(`${key}=${encodeURIComponent(String(value))}`);
}

function appendPageParams(params: string[], page?: number, pageSize?: number) {
  if (page !== undefined && Number.isFinite(page) && page > 0) {
    appendParam(params, 'page', page);
  }
  if (pageSize !== undefined && Number.isFinite(pageSize) && pageSize > 0) {
    appendParam(params, 'pageSize', pageSize);
  }
}

/** ONB-001 列表 query（status 逗号多值 / keyword / dateFrom / dateTo / page / pageSize）。 */
export function buildOnboardingsQuery(query: CorporateOnboardingListQuery = {}): string {
  const params: string[] = [];
  if (Array.isArray(query.status)) {
    const joined = query.status.filter((s) => s && s.trim()).join(',');
    if (joined) {
      appendParam(params, 'status', joined);
    }
  } else if (query.status && query.status.trim()) {
    appendParam(params, 'status', query.status.trim());
  }
  if (query.keyword && query.keyword.trim()) {
    appendParam(params, 'keyword', query.keyword.trim());
  }
  if (query.dateFrom && query.dateFrom.trim()) {
    appendParam(params, 'dateFrom', query.dateFrom.trim());
  }
  if (query.dateTo && query.dateTo.trim()) {
    appendParam(params, 'dateTo', query.dateTo.trim());
  }
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** ONB-006 企业客户列表 query（keyword / kybStatus / from / to / page / pageSize）。
 * from/to：开通时间区间（YYYY-MM-DD 闭区间，US-T3-1 已合入，CorporateOnboardingController.customers
 * 实锚 @RequestParam from/to → parseDate 与 ONB-001 dateFrom/dateTo 同一解析）。 */
export function buildCustomersQuery(query: CorporateCustomerListQuery = {}): string {
  const params: string[] = [];
  if (query.keyword && query.keyword.trim()) {
    appendParam(params, 'keyword', query.keyword.trim());
  }
  if (query.kybStatus && query.kybStatus.trim()) {
    appendParam(params, 'kybStatus', kybStatusCodeOf(query.kybStatus.trim()));
  }
  if (query.from && query.from.trim()) {
    appendParam(params, 'from', query.from.trim());
  }
  if (query.to && query.to.trim()) {
    appendParam(params, 'to', query.to.trim());
  }
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

// ================= 写操作头映射（If-Match / X-Confirm，供顺延轮 010~014 复用） =================

export type CorporateWriteHeaderOpts = {
  /** 乐观锁版本（并发冲突 1008）→ If-Match 头 */
  ifMatch?: string;
  /** 启停/删除类二次确认 → X-Confirm: true（corporate-api BUD-003 / RSK-002） */
  confirm?: boolean;
};

/** 写助手头映射：幂等键恒由 client.ts 注入（opts.idempotencyKey 复用同键），此处只补条件头。 */
export function buildCorporateWriteHeaders(opts: CorporateWriteHeaderOpts = {}): Record<string, string> {
  const headers: Record<string, string> = {};
  if (opts.ifMatch !== undefined && opts.ifMatch !== null && String(opts.ifMatch).trim() !== '') {
    headers['If-Match'] = String(opts.ifMatch).trim();
  }
  if (opts.confirm === true) {
    headers['X-Confirm'] = 'true';
  }
  return headers;
}

// ================= 状态枚举 → UI 文案（查 lib/merchantDictExt 字典；未知原样返回） =================
// 本文件保留既有函数签名，实现收敛为查表；未命中码回退原值、空值回退「—」行为不变。

export function mapOnboardingStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_ONBOARDING_STATUS[u]?.label ?? (status || '—');
}

export function mapPlatformStatusToUi(platformStatus?: string | null): string {
  const u = (platformStatus || '').toUpperCase();
  return CORP_PLATFORM_STATUS[u]?.label ?? (platformStatus || '—');
}

export function mapKybStatusToUi(kybStatus?: string | null): string {
  const u = (kybStatus || '').toUpperCase();
  return CORP_KYB_STATUS[u]?.label ?? (kybStatus || '—');
}

/** 材料齐备状态（COMPLETE / INCOMPLETE）。 */
export function mapDocStatusToUi(docStatus?: string | null): string {
  const u = (docStatus || '').toUpperCase();
  return CORP_DOC_STATUS[u]?.label ?? (docStatus || '—');
}

/** kybStatus 枚举名 → corporate_account.kyb_status TINYINT code（后端 @RequestParam Integer）。 */
export function kybStatusCodeOf(kybStatus: string): number {
  const u = kybStatus.toUpperCase();
  if (u === 'NONE') return 0;
  if (u === 'VALID') return 1;
  if (u === 'INCOMPLETE') return 2;
  if (u === 'REJECTED') return 3;
  const n = Number(u);
  return Number.isFinite(n) ? n : 0;
}

// ================= T1 查询构建（APR-001 / BUD-001 / BIL-001 / EXP-001 / RSK-001） =================

/** APR-001 审批单列表 query（status 默认由页面传 PENDING_APPROVAL；空参数不拼入）。 */
export type CorporateApprovalListQuery = {
  status?: string;
  memberId?: string;
  /** decimal string 透传（后端 BigDecimal） */
  amountMin?: string;
  amountMax?: string;
  /** ISO-8601 日期（YYYY-MM-DD，LocalDate 闭区间） */
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export function buildApprovalsQuery(query: CorporateApprovalListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) appendParam(params, key, raw.trim());
  };
  text('status', query.status);
  text('memberId', query.memberId);
  text('amountMin', query.amountMin);
  text('amountMax', query.amountMax);
  text('dateFrom', query.dateFrom);
  text('dateTo', query.dateTo);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** BUD-001 预算列表 query（budgetType 枚举名 / periodStart / periodEnd / 分页）。 */
export type CorporateBudgetListQuery = {
  budgetType?: string;
  periodStart?: string;
  periodEnd?: string;
  page?: number;
  pageSize?: number;
};

export function buildBudgetsQuery(query: CorporateBudgetListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) appendParam(params, key, raw.trim());
  };
  text('budgetType', query.budgetType);
  text('periodStart', query.periodStart);
  text('periodEnd', query.periodEnd);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** BIL-001 周期账单 query（periodType / periodStart / periodEnd / dimension 枚举名 / 分页）。 */
export type CorporateBillListQuery = {
  periodType?: string;
  periodStart?: string;
  periodEnd?: string;
  dimension?: string;
  page?: number;
  pageSize?: number;
};

export function buildBillsQuery(query: CorporateBillListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) appendParam(params, key, raw.trim());
  };
  text('periodType', query.periodType);
  text('periodStart', query.periodStart);
  text('periodEnd', query.periodEnd);
  text('dimension', query.dimension);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** EXP-001 报销单列表 query（status 枚举名 / memberId / dateFrom / dateTo / 分页）。 */
export type CorporateExpenseListQuery = {
  status?: string;
  memberId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export function buildExpensesQuery(query: CorporateExpenseListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) appendParam(params, key, raw.trim());
  };
  text('status', query.status);
  text('memberId', query.memberId);
  text('dateFrom', query.dateFrom);
  text('dateTo', query.dateTo);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** RSK-001 规则列表 query：ruleType/scope/status 后端收 TINYINT Integer code（空值不拼入）。 */
export type CorporateRiskRuleListQuery = {
  ruleType?: string;
  scope?: string;
  status?: string;
  page?: number;
  pageSize?: number;
};

export function buildRiskRulesQuery(query: CorporateRiskRuleListQuery = {}): string {
  const params: string[] = [];
  const code = (key: string, raw: string | undefined, toCode: (s: string) => number) => {
    const v = (raw || '').trim();
    if (v) appendParam(params, key, toCode(v));
  };
  code('ruleType', query.ruleType, ruleTypeCodeOf);
  code('scope', query.scope, ruleScopeCodeOf);
  code('status', query.status, ruleStatusCodeOf);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** MBR-002 成员列表 query（departmentId / status / role / keyword / 分页）。 */
export type CorporateMemberListQuery = {
  departmentId?: string;
  status?: string;
  role?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
};

export function buildMembersQuery(query: CorporateMemberListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) {
      appendParam(params, key, raw.trim());
    }
  };
  text('departmentId', query.departmentId);
  text('status', query.status);
  text('role', query.role);
  text('keyword', query.keyword);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

/** CARD-001 企业卡列表 query（departmentId / usageLabel / status / memberId / 分页）。 */
export type CorporateCardListQuery = {
  departmentId?: string;
  usageLabel?: string;
  status?: string;
  memberId?: string;
  page?: number;
  pageSize?: number;
};

export function buildCardsQuery(query: CorporateCardListQuery = {}): string {
  const params: string[] = [];
  const text = (key: string, raw?: string) => {
    if (raw && raw.trim()) {
      appendParam(params, key, raw.trim());
    }
  };
  text('departmentId', query.departmentId);
  text('usageLabel', query.usageLabel);
  text('status', query.status);
  text('memberId', query.memberId);
  appendPageParams(params, query.page, query.pageSize);
  return params.join('&');
}

// ================= 错误分类（US-T2-2：client.ts 失败分支已透出结构化 code） =================

/** 失败结果最小形态（对齐 client.ts ApiResult 失败分支；兼容纯文案历史形态） */
type CorporateFailureInput = { code?: string; error?: string } | string | null | undefined;

/**
 * 权限类错误识别（Edge #1：403 → 页面「无权限访问」）。
 * code 优先：后端 Result 信封业务码 AUTH_403（ErrorCode.java:101 FORBIDDEN，403 无门户后缀）。
 * 文案回退（无 code 的历史形态/HTTP 层错误）：AuthorizationDenied → HTTP 403 + message「无权限」；
 * 业务内跨商户 → message「无权访问其他商户的…」；transport 层「HTTP 403」。
 */
export function isForbiddenError(input: CorporateFailureInput): boolean {
  if (!input) return false;
  if (typeof input !== 'object') {
    return legacyForbiddenTextMatch(input);
  }
  const code = (input.code || '').toUpperCase();
  if (code === 'AUTH_403') return true;
  return legacyForbiddenTextMatch(input.error || '');
}

function legacyForbiddenTextMatch(error: string): boolean {
  if (!error) return false;
  return error.includes('AUTH_403') || error.includes('无权') || error.includes('HTTP 403');
}

/**
 * 决策失败文案映射（W1 裁决沿用：4517/4518/4502/4503 → toast 文案区分；
 * 前端不发明新码，未知错误展示后端 message 原文）。
 * 锚点取自 CorporateOnboardingAppService 实际抛错 message。
 */
export function mapDecisionErrorToMessage(error?: string | null): string {
  const raw = error || EMPTY_ERROR_FALLBACK;
  if (raw.includes('高风险')) {
    return '高风险行业禁止商户直接通过，须转平台审核（ESCALATE）';
  }
  if (raw.includes('KYC')) {
    return '法人或经办人 KYC 未通过，禁止通过；请确认 KYC 结论为 PASS 后重试';
  }
  if (raw.includes('Idempotency-Key')) {
    return '请求正在处理或幂等键冲突，请稍后刷新后重试';
  }
  // 4502 状态机非法 / 4503 缺件 / 其余：后端 message 已具语义，原样透出
  return raw;
}

/**
 * 平台备案重试失败文案（4514 retryable：本地开通不阻塞、状态不变）。
 */
export function mapFilingErrorToMessage(error?: string | null): string {
  const raw = error || '备案上报失败';
  if (raw.includes('备案')) {
    return `${raw}（本地开通不受影响，可重新上报）`;
  }
  return '平台备案上报失败，可重新上报（本地开通不受影响）';
}

// ================= T1 状态枚举 → UI 文案（查字典薄层，未知原样返回） =================

/** 审批单状态（CorpApprovalStatus）。 */
export function mapApprovalStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_APPROVAL_STATUS[u]?.label ?? (status || '—');
}

/** 预算类型（CorpBudgetType）。 */
export function mapBudgetTypeToUi(budgetType?: string | null): string {
  const u = (budgetType || '').toUpperCase();
  return CORP_BUDGET_TYPE[u]?.label ?? (budgetType || '—');
}

/** 预算状态（CorpBudgetStatus）。 */
export function mapBudgetStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_BUDGET_STATUS[u]?.label ?? (status || '—');
}

/** 报销单状态（CorpExpenseStatus）。 */
export function mapExpenseStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_EXPENSE_STATUS[u]?.label ?? (status || '—');
}

/** 风控规则类型（CorpRiskRuleType）。 */
export function mapRuleTypeToUi(ruleType?: string | null): string {
  const u = (ruleType || '').toUpperCase();
  return CORP_RISK_RULE_TYPE[u]?.label ?? (ruleType || '—');
}

/** 风控规则作用域（CorpRiskRuleScope）。 */
export function mapRuleScopeToUi(scope?: string | null): string {
  const u = (scope || '').toUpperCase();
  return CORP_RISK_RULE_SCOPE[u]?.label ?? (scope || '—');
}

/** 风控规则状态（后端 TINYINT ENABLED/DISABLED → 出参枚举名 ACTIVE/DISABLED）。 */
export function mapRuleStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_RISK_RULE_STATUS[u]?.label ?? (status || '—');
}

/** 账单聚合维度（CorpBillDimension）。 */
export function mapBillDimensionToUi(dimension?: string | null): string {
  const u = (dimension || '').toUpperCase();
  return CORP_BILL_DIMENSION[u]?.label ?? (dimension || '—');
}

/** 账单状态（CorporateBill 聚合状态常量 GENERATED/CONFIRMED）。 */
export function mapBillStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_BILL_STATUS[u]?.label ?? (status || '—');
}

/** 成员角色（CorpMemberRole）。 */
export function mapMemberRoleToUi(role?: string | null): string {
  const u = (role || '').toUpperCase();
  return CORP_MEMBER_ROLE[u]?.label ?? (role || '—');
}

/** 成员状态（CorpMemberStatus）。 */
export function mapMemberStatusToUi(status?: string | null): string {
  const u = (status || '').toUpperCase();
  return CORP_MEMBER_STATUS[u]?.label ?? (status || '—');
}

// ================= T1 枚举名 → TINYINT code（RSK-001 筛选 @RequestParam Integer） =================

/** 规则类型枚举名 → TINYINT code（CorpRiskRuleType：1~4）。 */
export function ruleTypeCodeOf(ruleType: string): number {
  const u = ruleType.trim().toUpperCase();
  if (u === 'MCC_WHITELIST') return 1;
  if (u === 'LIMIT_SINGLE') return 2;
  if (u === 'LIMIT_DAILY') return 3;
  if (u === 'LIMIT_MONTHLY') return 4;
  const n = Number(u);
  return Number.isFinite(n) ? n : 0;
}

/** 作用域枚举名 → TINYINT code（CorpRiskRuleScope：GLOBAL=1 / DEPARTMENT=2 / MEMBER=3 / CARD=4）。 */
export function ruleScopeCodeOf(scope: string): number {
  const u = scope.trim().toUpperCase();
  if (u === 'GLOBAL') return 1;
  if (u === 'DEPARTMENT') return 2;
  if (u === 'MEMBER') return 3;
  if (u === 'CARD') return 4;
  const n = Number(u);
  return Number.isFinite(n) ? n : 0;
}

/** 规则状态枚举名 → TINYINT code（CorporateRiskRule：STATUS_ENABLED=1 / STATUS_DISABLED=2）。 */
export function ruleStatusCodeOf(status: string): number {
  const u = status.trim().toUpperCase();
  if (u === 'ACTIVE') return 1;
  if (u === 'DISABLED') return 2;
  const n = Number(u);
  return Number.isFinite(n) ? n : 0;
}

// ================= T1 表单校验纯函数 =================

/** decimal 字符串（正数，最多两位小数；金额一律 decimal string 透传不换算）。 */
export function isPositiveDecimal(raw: string): boolean {
  return /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(raw.trim()) && /[1-9]/.test(raw);
}

/** decimal 字符串比较（a > b → 1；a < b → -1；相等 → 0）。 */
export function compareDecimal(a: string, b: string): number {
  const [ai, af = ''] = a.trim().split('.');
  const [bi, bf = ''] = b.trim().split('.');
  const aw = BigInt(ai || '0');
  const bw = BigInt(bi || '0');
  if (aw !== bw) return aw > bw ? 1 : -1;
  const afn = (af || '').padEnd(4, '0');
  const bfn = (bf || '').padEnd(4, '0');
  if (afn === bfn) return 0;
  return afn > bfn ? 1 : -1;
}

/** MCC 码校验：恰好 4 位数字。 */
export function isMccCode(raw: string): boolean {
  return /^\d{4}$/.test(raw.trim());
}

/** 新建预算表单校验（分类预算 ≤ 总预算剩余为前端预校验，后端 4507 兜底）。 */
export function validateBudgetForm(form: {
  budgetType: string;
  subjectKey?: string;
  periodStart?: string;
  periodEnd?: string;
  amount?: string;
  currency?: string;
}, totalBudgetRemain?: string): string | null {
  if (!form.budgetType) return '请选择预算类型';
  if (form.budgetType === 'CHANNEL_ACCOUNT' && !form.subjectKey) {
    return '渠道账户预算须选择分类主体';
  }
  if (!form.periodStart || !form.periodEnd) return '请选择预算周期';
  if (form.periodStart > form.periodEnd) return '周期开始日期不能晚于结束日期';
  if (!form.amount || !isPositiveDecimal(form.amount)) return '金额必须大于 0（最多两位小数）';
  if (!form.currency || !/^[A-Z]{3}$/.test(form.currency.trim().toUpperCase())) {
    return '币种须为 ISO-4217 三位字母';
  }
  // 分类预算不得超总预算剩余（前端预校验；仅 TOTAL 以外类型且能取到总预算剩余时）
  if (form.budgetType !== 'TOTAL' && totalBudgetRemain && isPositiveDecimal(totalBudgetRemain)) {
    if (compareDecimal(form.amount, totalBudgetRemain) === 1) {
      return '分类预算不得超过总预算剩余';
    }
  }
  return null;
}

/** 审批决策表单校验（拒绝 reason 必填 1–500；通过不要求 reason）。 */
export function validateApprovalDecision(decision: string, reason: string): string | null {
  if (decision !== 'APPROVE' && decision !== 'REJECT') return '请选择决策';
  if (decision === 'REJECT') {
    const r = reason.trim();
    if (!r) return '拒绝必须填写原因';
    if (r.length > 500) return '原因不能超过 500 字';
  }
  return null;
}

/** 报销发起表单校验（txIds ≥1 / invoice ≥1 / 金额 ≤ 关联交易合计）。 */
export function validateExpenseForm(form: {
  txIds: string[];
  invoiceFileCount: number;
  amount?: string;
}, txTotalAmount?: string): string | null {
  if (form.txIds.length === 0) return '请至少关联一笔交易';
  if (form.invoiceFileCount < 1) return '请至少上传 1 个 invoice 附件';
  if (!form.amount || !isPositiveDecimal(form.amount)) return '金额必须大于 0（最多两位小数）';
  if (txTotalAmount && isPositiveDecimal(txTotalAmount)
    && compareDecimal(form.amount, txTotalAmount) === 1) {
    return '报销金额不能超过关联交易合计';
  }
  return null;
}

/** 风控规则表单校验（MCC 4 位数字 / 限额 >0 / scope 非 GLOBAL 须 scopeId；收窄叠加语义不放松）。 */
export function validateRiskRuleForm(form: {
  ruleType: string;
  mccCodes: string[];
  limitAmount?: string;
  scope: string;
  scopeId?: string;
}): string | null {
  if (!form.ruleType) return '请选择规则类型';
  if (form.ruleType === 'MCC_WHITELIST') {
    if (form.mccCodes.length === 0) return 'MCC 白名单不能为空';
    for (const mcc of form.mccCodes) {
      if (!isMccCode(mcc)) return `MCC 码须为 4 位数字：${mcc}`;
    }
  } else {
    if (!form.limitAmount || !isPositiveDecimal(form.limitAmount)) {
      return '限额金额必须大于 0（最多两位小数）';
    }
  }
  if (!form.scope) return '请选择作用域';
  if (form.scope !== 'GLOBAL' && !form.scopeId) return '非全局作用域须选择作用对象';
  return null;
}

// ================= T1 错误文案映射（code 结构化优先，无 code 文案兜底） =================

/**
 * 预算域错误文案映射（HP-T1-3 Edge-1：4506/4507/4510/1008 四类，code 优先、文案兜底）。
 * 4510 为一码双义（预算周期重复 + 报销单状态非法），预算语境固定取周期重复语义；
 * 未知码透传后端 message 原文。
 */
export function mapBudgetErrorToMessage(code?: string, error?: string): string {
  const c = (code || '').trim();
  if (c === '4510') return '该分类本周期预算已存在';
  if (c === '4507') return '分类预算不得超过总预算剩余';
  if (c === '4506') return '预算不存在或已暂停，请刷新';
  if (c === '1008') return '版本冲突，请刷新后重试';
  // 文案兜底（无 code 历史形态）
  const raw = error || EMPTY_ERROR_FALLBACK;
  if (!c) {
    if (raw.includes('周期重复') || raw.includes('已存在')) return '该分类本周期预算已存在';
    if (raw.includes('超出总预算') || raw.includes('无预算归属')) return '分类预算不得超过总预算剩余';
    if (raw.includes('预算不存在或已暂停')) return '预算不存在或已暂停，请刷新';
    if (raw.includes('已变更，请刷新')) return '版本冲突，请刷新后重试';
  }
  return raw;
}

/**
 * 审批决策错误文案映射（HP-T1-2 Edge-2：4508/4509 前端展示后端 message 原文兜底；
 * 403 无权限；权限判定交 isForbiddenError，此处只管文案）。
 */
export function mapApprovalDecisionErrorToMessage(code?: string, error?: string): string {
  const raw = error || '决策失败';
  const c = (code || '').trim();
  if (c === '4508') return raw || '审批单不存在';
  if (c === '4509') return raw || '审批单非待审批状态';
  return raw;
}

/** 审批通过后重放失败固定文案（replayStatus=FAILED；审批单保持 APPROVED、无资金副作用）。 */
export const APPROVAL_REPLAY_FAILED_MESSAGE = '放行时余额/状态变化，交易未完成';

/**
 * 报销域错误文案映射（HP-T1-4：4511 重复关联；4510 双语义在报销语境展示后端 message 原文）。
 */
export function mapExpenseErrorToMessage(code?: string, error?: string): string {
  const c = (code || '').trim();
  if (c === '4511') return '该 invoice 已关联报销';
  const raw = error || EMPTY_ERROR_FALLBACK;
  if (!c) {
    if (raw.includes('已关联报销')) return '该 invoice 已关联报销';
  }
  return raw;
}

/**
 * 风控规则错误文案映射（HP-T1-5 Edge-1：4512 收窄拦截 / 4516 重复规则 / 1008 版本冲突 / 4502 透传）。
 */
export function mapRiskErrorToMessage(code?: string, error?: string): string {
  const c = (code || '').trim();
  if (c === '4512') return '不得宽于商户/平台风控';
  if (c === '4516') return '重复规则：同类型同作用域配置已存在';
  if (c === '1008') return '数据已变更，请刷新后重试';
  const raw = error || EMPTY_ERROR_FALLBACK;
  if (!c) {
    // 后端 4512 实况 message 两形态：「白名单超出商户/平台放行集」「限额宽于商户/平台既有限额」
    if (raw.includes('宽于父级') || raw.includes('宽于商户') || raw.includes('超出商户/平台')) {
      return '不得宽于商户/平台风控';
    }
    if (raw.includes('重复绑定') || raw.includes('已存在')) return '重复规则：同类型同作用域配置已存在';
    if (raw.includes('已变更，请刷新')) return '数据已变更，请刷新后重试';
  }
  return raw;
}

// ================= T1 展示辅助纯函数 =================

/** 审批超时真源文案（pages.md §2.9 空态与异常：EXPIRED 态剩余时间列展示）。 */
export const APPROVAL_EXPIRED_TEXT = '审批超时，已按拒绝处理';

/** 审批单默认超时窗口（分钟；expiresAt 缺失时以 submittedAt + 15min 兜底）。 */
export const APPROVAL_DEFAULT_TTL_MINUTES = 15;

/**
 * 审批单剩余时间展示（HP-T1-2：剩余时间列；EXPIRED → 真源超时文案）。
 * @param nowEpochMs 当前时间（node 可直测注入；缺省 Date.now()）
 */
export function approvalRemainingText(
  status: string | null | undefined,
  submittedAt: string | null | undefined,
  expiresAt: string | null | undefined,
  nowEpochMs?: number,
): string {
  const s = (status || '').toUpperCase();
  if (s === 'EXPIRED') return APPROVAL_EXPIRED_TEXT;
  if (s !== 'PENDING_APPROVAL') return '—';
  let endMs: number | null = null;
  if (expiresAt) {
    const parsed = Date.parse(expiresAt);
    if (Number.isFinite(parsed)) endMs = parsed;
  }
  if (endMs === null && submittedAt) {
    const start = Date.parse(submittedAt);
    if (Number.isFinite(start)) endMs = start + APPROVAL_DEFAULT_TTL_MINUTES * 60_000;
  }
  if (endMs === null) return `默认 ${APPROVAL_DEFAULT_TTL_MINUTES} 分钟`;
  const remain = endMs - (nowEpochMs ?? Date.now());
  if (remain <= 0) return APPROVAL_EXPIRED_TEXT;
  const minutes = Math.floor(remain / 60_000);
  const seconds = Math.floor((remain % 60_000) / 1000);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

type DepartmentNodeInput = {
  id: string;
  name: string;
  parentId: string | null;
  depth: number;
  managerMemberId: string | null;
};

/**
 * 部门树展平排序（HP-T1-1：≤5 级含负责人）：
 * 按 parent 链拓扑排序（根在前），每节点带 level（1 起）与缩进路径名；孤立节点（父缺失）按 id 兜底排在末尾。
 */
export function sortDepartmentNodes(
  nodes: DepartmentNodeInput[] | null | undefined,
): (DepartmentNodeInput & { level: number; pathName: string })[] {
  const list = nodes ?? [];
  const byId = new Map(list.map((n) => [n.id, n]));
  const out: (DepartmentNodeInput & { level: number; pathName: string })[] = [];
  const visited = new Set<string>();
  const visit = (node: DepartmentNodeInput, level: number, path: string, guard: Set<string>) => {
    if (visited.has(node.id) || guard.has(node.id)) return;
    guard.add(node.id);
    visited.add(node.id);
    const pathName = path ? `${path} / ${node.name}` : node.name;
    out.push({ ...node, level, pathName });
    for (const child of list.filter((n) => n.parentId === node.id)) {
      visit(child, level + 1, pathName, guard);
    }
  };
  for (const root of list.filter((n) => !n.parentId || !byId.has(n.parentId))) {
    visit(root, 1, '', new Set());
  }
  return out;
}

/** invoice 附件扩展名校验（EXP-003：jpeg/png/pdf；10MB 上限由文件对象 size 另行校验）。 */
export function isInvoiceFileNameAllowed(name: string): boolean {
  return /\.(jpe?g|png|pdf)$/i.test(name.trim());
}

/** invoice 单文件大小上限（10MB，EXP-003 契约）。 */
export const INVOICE_MAX_FILE_BYTES = 10 * 1024 * 1024;
